import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Box,
  Skeleton,
  WidgetBandHeader,
  WidgetEmpty,
  WidgetEmptyBands,
  WidgetSkeleton,
} from "@salestrak/ui";
import { UpdateAgeDialog } from "#app/components/age/update-age-dialog";
import { useIssueView } from "#app/components/issue-view";
import { IssueGroupTitle } from "#app/components/issues/issue-group-title";
import {
  UNASSIGNED_GROUP_KEY,
  type IssueTableGroupBy,
} from "#app/components/issues/issue-grouping";
import { ProjectListRow } from "#app/components/projects/project-list-row";
import { useIssueListMutations } from "#app/hooks/issue-list/use-issue-list-mutations";
import {
  useIssueQueryGroups,
  type IssueQueryGroup,
} from "#app/hooks/issue-list/use-issue-query-groups";
import { AGE_STATUS } from "#app/lib/age-status";
import type { IssueRow } from "#app/lib/data";
import type { ProjectRowFields } from "#app/widgets/summary/related-entity-widget";
import type { IssueTableQuery } from "./issue-table";
import { useBandFolds } from "./use-band-folds";
import { useIssueRowsPaged } from "./use-issue-rows-paged";
import type { Omittable } from "@salestrak/ui";

/**
 * Every qualifier on, fixed. A dashboard list is a triage queue: the age chip
 * is the fact the card is sorted and filtered by, so a reader's persisted
 * per-surface row prefs do not apply here.
 */
const LIST_ROW_FIELDS: ProjectRowFields = { stage: true, assignees: true, age: true };

/**
 * Age bands in the order a QUEUE reads them: the most-owed Follow up first,
 * the same direction its rows sort. The table's age bands keep the timeline
 * order (On track → Update soon → Stale) because a grid is read as a progression;
 * a queue is read top-down as "what do I owe first". Updates-off is the null
 * bucket (the server has no fourth status), owed nothing, so it closes the
 * queue. Keys the list does not know sort after these.
 */
const AGE_URGENCY: readonly string[] = [
  AGE_STATUS.offTrack,
  AGE_STATUS.atRisk,
  AGE_STATUS.onTrack,
  UNASSIGNED_GROUP_KEY,
];

function ageUrgency(key: string): number {
  const index = AGE_URGENCY.indexOf(key);
  return index === -1 ? AGE_URGENCY.length : index;
}

/**
 * One band a grouped list always draws, named by the server group key it
 * merges with: `key` is the `issueGroupCounts` key the aggregation answers
 * under, `label` the band's title when no group answers.
 */
export type IssueListBand = { key: string; label: string };

export type IssueListProps = Omittable<{
  /** The fully-folded base `where` + server ordering — the SAME query the
   *  table twin pages over (`useEntityList`), never a list-local fold. */
  query: IssueTableQuery;
  /** Server-grouped bands over the same folded `where` — each band is its own
   *  paged query, exactly as the table twin's groups are. `none` (default) is
   *  one flat stack. */
  groupBy?: IssueTableGroupBy;
  /** The queue shape, for a list ordered by the Follow up: the follow-up chip
   *  leads each row and the deadline trails it (`ProjectListRow`'s
   *  `ageLeads`), and age bands order most-owed first. */
  ageLeads?: boolean;
  /** Band headers fold their rows — a disclosure per band, on the card and in
   *  the maximized view alike. Ignored ungrouped. */
  collapsible?: boolean;
  emptyTitle: string;
  emptyDescription?: string;
  /** The empty state's one door, forwarded to `WidgetEmpty` — a `WidgetEmptyAction`. */
  emptyAction?: React.ReactNode;
  /** The bands a grouped list ALWAYS draws, whatever the aggregation answers:
   *  the grouping the body would have drawn, known before any row loads. A
   *  band the counts fill merges by `key`; one they leave empty stands at zero
   *  as a muted disclosure, so a reader sees every band the card watches. All
   *  at zero, the list is `WidgetEmptyBands` in place of the title-only
   *  empty, with `emptyNote` as its one faint line. */
  emptyBands?: readonly IssueListBand[];
  emptyNote?: string;
}>;

/** The live-row handlers every band's rows share. */
type IssueListRowHandlers = {
  ageLeads: boolean;
  onOpen: (issue: IssueRow) => void;
  onAgeClick: (issue: IssueRow) => void;
  onAssigneesChange: (issueId: string, entityIds: string[]) => void;
};

/**
 * `IssueList` — the list-shaped twin of `IssueTable`: the scoped projects as a
 * stack of `ProjectListRow`s (stage glyph, name, then a trailing rail of
 * assignees + the follow-up age), the SAME row every Projects module renders,
 * so a project row reads one way everywhere. Rows stay
 * live: press opens the project, the avatar stack opens the assignee picker,
 * the age chip opens the update dialog.
 *
 * Grouped, the band SET comes from the exact server `issueGroupCounts`
 * aggregation over the folded `where` (`useIssueQueryGroups`, the table's own
 * machinery) — never from a loaded sample — and each band pages its own rows
 * through `useIssueRowsPaged`, so every project in every band is reachable
 * through the card's scroll. A band's title is the dimension's chip for an
 * entity dimension (`IssueGroupTitle`) and the plain status word for age, with
 * the band's exact count beside it — the grid's group header vocabulary.
 * Under `collapsible` each band header is a disclosure that folds its rows
 * (a folded band unmounts its paged query, so a long Stale run costs nothing
 * while the reader works Update soon); a fold is remembered per placed
 * widget, per browser (`useBandFolds`), so a reload finds the bands as the
 * reader left them.
 *
 * A band named in `emptyBands` is drawn whether or not the aggregation counted
 * rows into it: at zero it is a muted header with no query behind it, on the
 * same disclosure the live bands use, so the card's structure never comes and
 * goes with the counts. Bands the aggregation answers beyond that set follow
 * in the list's order.
 */
export function IssueList({
  query,
  groupBy = "none",
  ageLeads = false,
  collapsible = false,
  emptyTitle,
  emptyDescription,
  emptyAction,
  emptyBands,
  emptyNote,
}: IssueListProps) {
  const { openIssue } = useIssueView();
  const { updateAssignees } = useIssueListMutations();
  const [ageDialogIssue, setAgeDialogIssue] = useState<IssueRow | null>(null);
  const handleAssigneesChange = useCallback(
    (issueId: string, entityIds: string[]) => {
      void updateAssignees(issueId, entityIds);
    },
    [updateAssignees],
  );
  const handleOpen = useCallback((issue: IssueRow) => openIssue(issue, { source: "widget" }), [openIssue]);
  const handlers = useMemo<IssueListRowHandlers>(
    () => ({
      ageLeads,
      onOpen: handleOpen,
      onAgeClick: setAgeDialogIssue,
      onAssigneesChange: handleAssigneesChange,
    }),
    [ageLeads, handleOpen, handleAssigneesChange],
  );

  const dimension = groupBy === "none" ? null : groupBy;
  const { groups, ready } = useIssueQueryGroups({ where: query.where, dimension });
  // Folded band keys, remembered per card: a reader who put Stale away
  // finds it still away after a reload.
  const { folded: bandFolded, toggle: toggleBand } = useBandFolds();
  const orderedGroups = useMemo<IssueQueryGroup[] | null>(() => {
    if (!groups) return null;
    if (!ageLeads || dimension !== "age") return groups;
    return [...groups].sort((a, b) => ageUrgency(a.key) - ageUrgency(b.key));
  }, [groups, ageLeads, dimension]);

  // The host module is `bleed`, so the zero bands restore the same `md` inset
  // the live bands do — their hairlines end where the rows' would. Grouped
  // and collapsible, each zero band is the same disclosure a live band is,
  // folding on the same remembered key.
  const foldable = Boolean(dimension) && collapsible;
  const empty = emptyBands ? (
    <Box direction="column" paddingX="md">
      <WidgetEmptyBands
        action={emptyAction}
        bands={emptyBands.map((band) => ({
          key: band.key,
          label: band.label,
          ...(foldable
            ? { expanded: !bandFolded(band.key), onToggle: () => toggleBand(band.key) }
            : {}),
        }))}
        note={emptyNote}
      />
    </Box>
  ) : (
    <WidgetEmpty
      title={emptyTitle}
      description={emptyDescription}
      action={emptyAction}
    />
  );
  const skeleton = (
    <Box fill="flex" paddingX="md">
      <WidgetSkeleton />
    </Box>
  );

  let body: ReactNode;
  if (!dimension) {
    body = (
      <IssueListBand query={query} handlers={handlers} loadingState={skeleton} emptyState={empty} />
    );
  } else if (!ready || !orderedGroups) {
    body = skeleton;
  } else {
    // The fixed bands lead, each the live group under its key when the
    // aggregation counted rows into it and a zero band otherwise; the groups
    // the fixed set does not name follow, already in the list's order.
    const fixed = emptyBands ?? [];
    const fixedKeys = new Set(fixed.map((band) => band.key));
    const known = new Map(orderedGroups.map((group) => [group.key, group]));
    const bands: IssueQueryGroup[] = [
      ...fixed.map(
        (band) =>
          known.get(band.key) ?? { key: band.key, label: band.label, totalCount: 0, where: undefined },
      ),
      ...orderedGroups.filter((group) => !fixedKeys.has(group.key)),
    ];
    body = bands.every((band) => band.totalCount === 0)
      ? empty
      : bands.map((group) => {
          const zero = group.totalCount === 0;
          const folded = collapsible && bandFolded(group.key);
          const header = (
            <IssueListBandHeader
              dimension={dimension}
              groupKey={group.key}
              label={group.label}
              count={group.totalCount}
              muted={zero}
              expanded={!folded}
              {...(collapsible ? { onToggle: () => toggleBand(group.key) } : {})}
            />
          );
          // A folded band is its header alone: the rows' query never mounts, so
          // folding a long band is also what makes the rest of the list cheap. A
          // zero band is the same line, muted: nothing would answer its query.
          if (folded || zero) {
            return (
              <Box key={group.key} direction="column" paddingX="md">
                {header}
              </Box>
            );
          }
          return (
            <IssueListBand
              key={group.key}
              query={{ where: group.where, orderBy: query.orderBy }}
              handlers={handlers}
              header={header}
              // A band exists because the aggregation counted rows into it, so a
              // page that answers empty is a race with an edit, not a state to
              // paint: the band drops until the counts re-answer.
              emptyState={null}
              loadingState={
                <Box direction="column" paddingX="md">
                  {header}
                  <Box paddingY="sm">
                    <Skeleton className="h-4 w-full" />
                  </Box>
                </Box>
              }
            />
          );
        });
  }

  return (
    <>
      {body}
      {ageDialogIssue ? (
        <UpdateAgeDialog
          via="widget_list"
          age={ageDialogIssue.age}
          issueId={ageDialogIssue.issueId}
          onOpenChange={(next) => {
            if (!next) setAgeDialogIssue(null);
          }}
          open
          stageId={ageDialogIssue.stageId}
        />
      ) : null}
    </>
  );
}

/**
 * A band's title line — the kit's `WidgetBandHeader`, so a live band and a
 * zero band share one geometry. Age bands are plain
 * words (the table renders them the same way); the entity dimensions render
 * the group's own chip so a stage band wears its pie, with the group's label
 * as the toggle's spoken name.
 */
function IssueListBandHeader({
  dimension,
  groupKey,
  label,
  count,
  muted = false,
  expanded,
  onToggle,
}: {
  dimension: Exclude<IssueTableGroupBy, "none">;
  groupKey: string;
  label: string;
  count: number;
  /** The zero band's weight: the name on the muted ink, the count faint. */
  muted?: boolean;
  expanded?: boolean;
  onToggle?: (() => void) | undefined;
}) {
  return (
    <WidgetBandHeader
      count={count}
      muted={muted}
      label={
        dimension === "age" ? label : <IssueGroupTitle dimension={dimension} groupKey={groupKey} />
      }
      name={label}
      {...(expanded === undefined ? {} : { expanded })}
      {...(onToggle ? { onToggle } : {})}
    />
  );
}

/**
 * One paged stack of rows — the whole list when ungrouped, one band when
 * grouped. Reads `useIssueRowsPaged` over its own folded query: the first page
 * loads eagerly and a sentinel after the last row appends the next as it
 * scrolls into view. The card chrome owns the scroller (this body is not
 * `body: "flush"`), so an observer — not a scroll handler — is what reaches it.
 */
function IssueListBand({
  query,
  handlers,
  header,
  loadingState,
  emptyState,
}: {
  query: IssueTableQuery;
  handlers: IssueListRowHandlers;
  header?: ReactNode;
  loadingState: ReactNode;
  emptyState: ReactNode;
}) {
  const { rows, loading, loadingMore, hasMore, loadMore } = useIssueRowsPaged(query);

  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    if (!hasMore || loadingMore) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) loadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadMore]);

  if (loading) return loadingState;
  if (rows.length === 0) return emptyState;

  return (
    // The host module is `bleed` for its table twin, so the content inset the
    // rows expect is restored here — the same `md` the table's `paddingX`
    // restores. Each `WidgetListRow` then bleeds its hover wash `sm` back into
    // that inset, exactly as it does inside a padded body, and the wash stays
    // clear of the card's border. The list scrolls in the card's own
    // scroller, which appends the scroll-end slack after it.
    <Box direction="column" paddingX="md">
      {header}
      {rows.map((issue) => (
        <ProjectListRow
          key={issue.issueId}
          ageLeads={handlers.ageLeads}
          fields={LIST_ROW_FIELDS}
          issue={issue}
          name={issue.name}
          onAgeClick={handlers.onAgeClick}
          onAssigneesChange={handlers.onAssigneesChange}
          onOpen={() => handlers.onOpen(issue)}
          projectId={issue.issueId}
          workflowId={issue.workflowId}
        />
      ))}
      {hasMore ? <Box ref={sentinelRef} height={1} shrink={false} /> : null}
      {loadingMore ? (
        <Box paddingY="sm">
          <Skeleton className="h-4 w-full" />
        </Box>
      ) : null}
    </Box>
  );
}
